import { useState } from "react";

import {
  settingsParamsOf,
  writableKeysOf,
  type McpSettings as McpSettingsData,
} from "../../api/mcp";
import { SETTING_RULES, validateSetting } from "../../api/settingsRules";
import { useAccess } from "../../auth/useAccess";
import { Gate } from "../../components/authority/Gate";
import { EditableSetting } from "../../components/settings/EditableSetting";
import {
  SETTING_CARDS,
  settingCardOf,
  settingEffectText,
  settingLabelOf,
} from "../../components/settings/settingsCopy";
import { Breadcrumbs } from "../../components/Layout";
import { ResourceView } from "../../components/states";
import { Card, Notice, Surface, Panel, SaveBar } from "../../components/ui";
import { ConfirmDialog } from "../../components/write/ConfirmDialog";
import { WriteFailure } from "../../components/write/WriteFailure";
import type { AppConfig } from "../../config";
import { useResource } from "../../hooks/useResource";
import { useWrite } from "../../hooks/useWrite";
import { Button } from "../../f7";

// Cards come from the console's own copy (components/settings/settingsCopy.ts),
// keyed by SSM path: Messages, Safety, Audit, Responses, and Other for a key
// the copy does not know. No card-wide timing note: timing differs per key
// (`response/max_chars` is live in ~5 minutes while the templates beside it
// wait for Apply), so each field states its own, from its own `effect`.
//
// No "Rate limits" entry on purpose. Rate limits are per tier, in each tier's
// quota document (PATCH /admin/quota/{tier}); the four old global
// `ratelimit/*` keys are retired and no longer listed by GET /admin/settings.

interface SettingsResponse {
  applied?: Record<string, { value: string; effect: string }>;
  staged?: string[];
  note?: string;
}

interface ApplyResponse {
  cluster?: string;
  service?: string;
  deployment_id?: string;
  status?: string;
  note?: string;
}

/**
 * `embedded` renders the body WITHOUT its own <Page> heading, for when this is
 * a panel inside another surface rather than a tab of its own. The console's
 * nav is eight tabs, so several real surfaces
 * live inside another one — see nav/sections.ts.
 *
 * `onWritten` runs after a Save or an Apply MCP accepted, for a surface that
 * shows something those writes change — the page's MCP admin-actions tile,
 * which otherwise stayed on its first read until the operator navigated away
 * (DA-3818).
 */
export function Settings({
  config,
  embedded = false,
  onWritten,
}: {
  config: AppConfig;
  embedded?: boolean;
  onWritten?: () => void;
}) {
  const { state, reload } = useResource<McpSettingsData>(config, "/mcp/admin/settings");
  const access = useAccess();
  const write = useWrite<SettingsResponse>(config);
  const apply = useWrite<ApplyResponse>(config);

  const [edits, setEdits] = useState<Record<string, string>>({});
  const [confirming, setConfirming] = useState(false);
  const [applying, setApplying] = useState(false);
  // Held in memory only, like the token — a reload loses it. See the note in
  // the banner: GET /admin/settings has no `staged` field to rebuild it from.
  const [staged, setStaged] = useState<string[]>([]);
  const [applied, setApplied] = useState<ApplyResponse | null>(null);

  const isProd = config.env === "prod";

  const dirty = Object.entries(edits);
  // MCP's own `writable[]` decides what may be written. A key in it that the
  // console has no rule for is plain text, not "not writable" (DA-3821).
  const writableKeys = new Set(state.kind === "ready" ? writableKeysOf(state.data) : []);
  const errors = dirty
    .map(([k, v]) => [k, validateSetting(k, v, { writable: writableKeys.has(k) })] as const)
    .filter(([, e]) => e !== null);

  function onSave() {
    // PATCH /admin/settings is `{ values }` only (`extra=forbid`). A typed
    // reason would 422. MCP writes one audit row per key with that key's
    // before/after — that is the trail, not a reason field.
    const values: Record<string, string | number | boolean> = {};
    for (const [k, v] of dirty) {
      const rule = SETTING_RULES[k];
      values[k] =
        rule?.kind === "number" ? Number(v) : rule?.kind === "boolean" ? v === "true" : v;
    }
    void write
      .submit("/mcp/admin/settings", { method: "PATCH", body: JSON.stringify({ values }) })
      .then((r) => {
        if (!r) return;
        setStaged((prev) => [...new Set([...prev, ...(r.staged ?? [])])]);
        setEdits({});
        setConfirming(false);
        reload();
        onWritten?.();
      });
  }

  function onApply() {
    // `confirm` is constructed HERE, never as a form default. The API defaults
    // it to false precisely so a restart cannot fire by omission.
    void apply
      .submit("/mcp/admin/settings/apply", {
        method: "POST",
        body: JSON.stringify({ confirm: true }),
      })
      .then((r) => {
        if (!r) return;
        setApplied(r);
        setStaged([]);
        setApplying(false);
        onWritten?.();
      });
  }

  return (
    <Surface embedded={embedded} title="Settings" subtitle="MCP's runtime configuration.">
      {/* Only when this is its own page. Embedded under Feature flags it
          rendered "Overview › Settings" inside another surface, naming a
          location the reader is not at. */}
      {!embedded && (
        <Breadcrumbs trail={[{ label: "Overview", to: "/" }, { label: "Settings" }]} />
      )}

      <ResourceView state={state} what="settings" onRetry={reload} />

      {state.kind === "ready" &&
        (() => {
          const s = state.data;
          const writable = writableKeys;
          const params = settingsParamsOf(s);
          const readOnly = params.filter((p) => !writable.has(p.key));

          return (
            <>
              {/* MCP's `staged` already leaves out the live keys (its
                  LIVE_KEYS), so the banner never lists one that needs no
                  Apply — listing those would train people to ignore it. */}
              {staged.length > 0 && (
                <div className="mb-4">
                  <Notice tone="amber" title="Changed, but not yet live">
                    <p>
                      {staged.map((k) => (
                        <span key={k} className="mr-3 text-xs">
                          {settingLabelOf(k)}
                        </span>
                      ))}
                    </p>
                    <p className="mt-1 text-xs">
                      These reach merchants only after Apply changes, or the next deploy.
                      This list lives in the page — a reload will lose it.
                    </p>
                  </Notice>
                </div>
              )}

              <Notice title="When a change takes effect">
                Each setting says when a change reaches merchants. Some are live within
                minutes with no restart; the others wait for <strong>Apply changes</strong>{" "}
                below, or the next deploy.
              </Notice>

              {/* An UNCATEGORISED writable key still gets a group ("Other").
                  Any setting MCP adds under a new prefix would otherwise be
                  writable on the API and invisible here — the console would
                  silently omit a knob rather than show one it cannot
                  describe. */}
              {SETTING_CARDS.map((title) => ({
                title,
                rows: params.filter((p) => writable.has(p.key) && settingCardOf(p.key) === title),
              }))
                .filter((g) => g.rows.length > 0)
                .map((g) => (
                  <Card key={g.title} title={g.title}>
                    <div className="flex flex-col gap-2">
                      {g.rows.map((p) => (
                        <EditableSetting
                          key={p.key}
                          param={p}
                          value={edits[p.key] ?? p.value ?? ""}
                          dirty={p.key in edits}
                          writable={writable.has(p.key)}
                          canWrite={access.can("settings:write")}
                          onChange={(v) =>
                            setEdits((e) =>
                              v === (p.value ?? "")
                                ? Object.fromEntries(
                                    Object.entries(e).filter(([k]) => k !== p.key),
                                  )
                                : { ...e, [p.key]: v },
                            )
                          }
                        />
                      ))}
                    </div>
                  </Card>
                ))}


              {dirty.length > 0 && (
                <SaveBar className="mt-6">
                    <p className="text-sm">
                      <span className="font-medium">{dirty.length}</span> change
                      {dirty.length === 1 ? "" : "s"} not saved
                      {errors.length > 0 && (
                        <span className="ml-2 text-red-600 dark:text-red-400">
                          · {errors.length} would be refused
                        </span>
                      )}
                    </p>
                    <div className="flex gap-2">
                      <Button outline onClick={() => setEdits({})}>
                        Discard
                      </Button>
                      <Gate need="settings:write">
                        <Button onClick={() => setConfirming(true)} disabled={errors.length > 0}>
                          Review {dirty.length} change{dirty.length === 1 ? "" : "s"}
                        </Button>
                      </Gate>
                    </div>
                  </SaveBar>
              )}

              <section className="mt-6">
                {/* The console's own collapsible Card, rather than the raw
                    <details> this used to hand-roll — same behaviour, and it
                    matches every other foldable block. */}
                {/* Folds on its own page only. Embedded, it sits inside
                    MCP's product block, which is already the fold. */}
                <Card
                  title={`Read-only parameters (${readOnly.length})`}
                  collapsible={!embedded}
                >
                  <p className="mb-2 text-xs text-slate-500 dark:text-slate-400">
                    Changing any of these would repoint storage or break auth and serving,
                    so the API refuses them. Secrets are excluded entirely and never read.
                  </p>
                  <div className="space-y-1">
                    {readOnly.map((p) => (
                      <Panel key={p.key} className="px-4 py-2">
                        <div className="flex flex-wrap items-baseline gap-x-3 text-xs">
                          <span className="ref">{p.key}</span>
                          <span className="ref break-all text-slate-500 dark:text-slate-400">
                            {p.exists ? p.value : "not seeded — code default applies"}
                          </span>
                        </div>
                      </Panel>
                    ))}
                  </div>
                </Card>
              </section>

              {/* Its own panel, visually separate from Save. A mis-click on
                  Save stages a value; a mis-click here restarts live traffic. */}
              <section className="mt-8">
                <Panel className="border-l-2 border-l-red-700 p-4 dark:border-l-red-400">
                  <h2 className="text-sm font-semibold">Apply changes to the running service</h2>
                  <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                    Rolling restart of the merchant-facing MCP service so tasks pick up new
                    configuration. Not a code deploy — the existing deployment circuit
                    breaker rolls back on failure.
                  </p>
                  <p className="mt-2 text-sm text-amber-700 dark:text-amber-400">
                    Apply is not scoped to your changes. It restarts the service, which
                    picks up <strong>every</strong> value changed by anyone since the last
                    restart.
                  </p>

                  {applied && (
                    <div className="mt-3">
                      <Notice tone="amber" title="Restart started">
                        <p>
                          Service <span className="ref">{applied.service}</span>, deployment{" "}
                          <span className="ref">{applied.deployment_id}</span> ({applied.status}).
                        </p>
                        <p className="mt-1 text-xs">
                          Tasks pick up the new values as they come up. There is no way to
                          observe completion from here — this says started, not done.
                        </p>
                      </Notice>
                    </div>
                  )}
                  <div className="mt-3">
                    <Gate need="service:restart">
                      <Button color="red" onClick={() => setApplying(true)}>
                        Apply changes…
                      </Button>
                    </Gate>
                  </div>
                </Panel>
              </section>

              <ConfirmDialog
                open={confirming}
                title={`Save ${dirty.length} setting${dirty.length === 1 ? "" : "s"}`}
                confirmLabel="Save"
                requireReason={false}
                busy={write.state.kind === "submitting"}
                error={<WriteFailure state={write.state} />}
                onCancel={() => setConfirming(false)}
                onConfirm={() => onSave()}
                effect={
                  // Per key, from each key's own `effect`: one sentence for
                  // the batch was wrong about at least one of them.
                  <ul className="space-y-1">
                    {dirty.map(([k]) => {
                      const p = params.find((x) => x.key === k);
                      return (
                        <li key={k}>
                          <span className="font-medium">{settingLabelOf(k)}</span>:{" "}
                          {settingEffectText(p?.effect) || "timing not stated"}
                        </li>
                      );
                    })}
                  </ul>
                }
              >
                <p>Nothing is written unless every value below is accepted.</p>
                <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                  MCP records each key&rsquo;s before and after on the audit log. There is no
                  reason field on this route.
                </p>
                <ul className="mt-2 space-y-1">
                  {dirty.map(([k, v]) => (
                    <li key={k} className="text-xs">
                      <span className="font-medium">{settingLabelOf(k)}</span>{" "}
                      <span className="ref">{k}</span>
                      <div className="ref mt-0.5 break-all text-slate-950 dark:text-white">{v}</div>
                    </li>
                  ))}
                </ul>
              </ConfirmDialog>

              <ConfirmDialog
                open={applying}
                title="Restart the merchant-facing service"
                confirmLabel={isProd ? "Restart production" : "Restart"}
                destructive
                // L3 in prod only: restarting the live service. The other case is
                // the fleet-wide domain and plugin gates (`every business`).
                // If everything needed one, none would matter.
                typeToConfirm={isProd ? "foodics-mcp-foodics-prod" : undefined}
                busy={apply.state.kind === "submitting"}
                error={<WriteFailure state={apply.state} />}
                onCancel={() => setApplying(false)}
                onConfirm={onApply}
                effect="Tasks restart in a rolling deployment; the circuit breaker rolls back on failure. There is no way to observe completion from this console."
              >
                <p>
                  This restarts the live merchant-facing MCP service
                  {isProd && (
                    <>
                      {" "}
                      <strong className="text-red-600 dark:text-red-400">in production — real merchants</strong>
                    </>
                  )}
                  .
                </p>
                <p>
                  It picks up <strong>every</strong> configuration value changed since the
                  last restart, not only yours.
                </p>
                {staged.length === 0 && (
                  <p className="text-amber-700 dark:text-amber-400">
                    You have nothing staged — but that does not make this a no-op. Anything
                    another person changed will go live too.
                  </p>
                )}
              </ConfirmDialog>
            </>
          );
        })()}
    </Surface>
  );
}
